import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CircleCheck, Clock, Link2Off, Pencil, ReceiptText, RefreshCw,
} from 'lucide-react';
import AddressDetails, { toBlockLetterAddress } from '../../components/address/AddressDetails';
import Spinner from '../../components/spinner/spinner';
import orderGateHook from '../../api/orderGateHook';
import { addressLinkService } from '../../api/addressLinkService';
import { ApiError } from '../../api/api';
import { ToastContext } from '../../contexts/toast-context-provider';
import { clearAddressLinkToken, getAddressLinkToken } from '../../helpers/address-link-token';

// The page a centre's "Send address link" opens: the student views, adds or
// updates their address (stored as the Billing Address their GST invoice
// prints). No sign-in - the link (its token, captured by
// helpers/address-link-token) is the credential, and it opens this student's
// address and nothing else. It lives 30 minutes from when it was sent and can
// be used again inside that time to correct a mistake.

const EMPTY_ADDRESS = {
  houseNo: '', area: '', city: '', state: '', pinCode: '', landmark: '',
};

// Matches what the server's link route accepts.
const schema = z.object({
  billing: z.object({
    houseNo: z.string().trim().min(1, 'Required').max(120, 'Too long'),
    area: z.string().trim().min(2, 'Must be at least 2 characters').max(200, 'Too long'),
    city: z.string().trim().min(2, 'Must be at least 2 characters').max(80, 'Too long'),
    state: z.string().min(1, 'Required'),
    pinCode: z.string().regex(/^\d{6}$/, 'Enter a 6-digit PIN code'),
    landmark: z.string().max(120, 'Too long').optional(),
  }),
});

const DEAD_LINK = {
  ERR_ADDRESS_LINK_EXPIRED: {
    title: 'This link has expired',
    body: 'Address links work for 30 minutes. Please ask the centre to send you a new one.',
  },
  ERR_ADDRESS_LINK_SUPERSEDED: {
    title: 'A newer link was sent',
    body: 'The centre has sent you a newer address link. Please open the latest one from your email, or scan the new QR code.',
  },
  ERR_ADDRESS_LINK_INVALID: {
    title: 'This link is not valid',
    body: 'Please ask the centre to send you a new address link.',
  },
};

const formatDate = (value) => (value
  ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : null);

function formatAddressLines(address) {
  if (!address) return [];
  return [
    [address.houseNo, address.area].filter(Boolean).join(', '),
    address.landmark,
    [address.city, address.state].filter(Boolean).join(', '),
    address.pinCode ? `PIN ${address.pinCode}` : null,
  ].filter(Boolean);
}

// Milliseconds left on the link by the server's clock: the offset measured when
// the page loaded absorbs a phone whose clock is off.
function useTimeLeft(expiresAt, clockOffset) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);
  if (!expiresAt) return null;
  return Math.max(0, expiresAt - (now + clockOffset));
}

function formatTimeLeft(ms) {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function PageShell({ children }) {
  return (
    <div className="min-h-[calc(100vh-56px)] bg-white md:bg-[#F7F8FA]">
      <div className="max-w-xl mx-auto px-4 py-6 md:py-10">{children}</div>
    </div>
  );
}
PageShell.propTypes = { children: PropTypes.node.isRequired };

function MessageCard({
  icon: Icon, tone, title, body, action,
}) {
  const toneClass = tone === 'success' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600';
  return (
    <section className="bg-white border border-[#B8E5FF] rounded-xl shadow-sm p-6 text-center">
      <div className={`mx-auto mb-4 w-14 h-14 rounded-full flex items-center justify-center ${toneClass}`}>
        <Icon className="w-7 h-7" aria-hidden="true" />
      </div>
      <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
      <p className="mt-2 text-sm text-gray-600">{body}</p>
      {action && <div className="mt-5">{action}</div>}
    </section>
  );
}
MessageCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  tone: PropTypes.oneOf(['success', 'warning']),
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  action: PropTypes.node,
};

const PRIMARY_BUTTON = 'inline-flex items-center justify-center gap-2 w-full md:w-auto min-h-11 px-5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors';
const SECONDARY_BUTTON = 'inline-flex items-center justify-center gap-2 w-full md:w-auto min-h-11 px-5 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors';

function BillingAddressPage() {
  const { orgId } = useParams();
  const { showToast } = useContext(ToastContext);
  const [states] = orderGateHook.useGetStatesList({ countryCode: 'IN' });
  const [token] = useState(() => getAddressLinkToken());

  // loading | ready | saved | dead | error | no-token
  const [phase, setPhase] = useState(token ? 'loading' : 'no-token');
  const [deadCode, setDeadCode] = useState(null);
  const [context, setContext] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userDetails, setUserDetails] = useState({ billing: EMPTY_ADDRESS });
  const [clockOffset, setClockOffset] = useState(0);
  const seededFrom = useRef(null);
  const timeLeft = useTimeLeft(context?.expiresAt, clockOffset);

  const {
    register, handleSubmit, setValue, formState: { errors },
  } = useForm({ resolver: zodResolver(schema), mode: 'onChange' });

  const endLink = useCallback((code) => {
    clearAddressLinkToken();
    setDeadCode(DEAD_LINK[code] ? code : 'ERR_ADDRESS_LINK_INVALID');
    setPhase('dead');
  }, []);

  const handleFailure = useCallback((err, { onOther }) => {
    if (err instanceof ApiError && err.status === 410) {
      endLink(err.code);
      return;
    }
    onOther(err);
  }, [endLink]);

  const load = useCallback(async () => {
    if (!token) return;
    setPhase('loading');
    try {
      const data = await addressLinkService.getContext(token);
      // A link is for one organisation's page; anything else is not this link.
      if (data?.orgId !== orgId) {
        endLink('ERR_ADDRESS_LINK_INVALID');
        return;
      }
      setClockOffset((data.serverTime || Date.now()) - Date.now());
      setContext(data);
      setEditing(!data.billingAddress || !data.isComplete);
      setPhase('ready');
    } catch (err) {
      handleFailure(err, { onOther: () => setPhase('error') });
    }
  }, [token, orgId, endLink, handleFailure]);

  useEffect(() => {
    load();
  }, [load]);

  // Seeds the form from the address on file, once the state list is there to
  // turn a stored state name into the option's code.
  const seedForm = useCallback((address) => {
    const stateCode = address?.stateCode
      || (states || []).find((st) => String(st.name).toLowerCase() === String(address?.state || '').toLowerCase())?.stateCode
      || '';
    const seed = toBlockLetterAddress({
      ...EMPTY_ADDRESS,
      houseNo: address?.houseNo || '',
      area: address?.area || '',
      city: address?.city || '',
      pinCode: address?.pinCode || '',
      landmark: address?.landmark || '',
      state: stateCode,
    });
    setUserDetails({ billing: seed });
    Object.entries(seed).forEach(([field, value]) => setValue(`billing.${field}`, value));
  }, [states, setValue]);

  useEffect(() => {
    if (!editing || !states?.length) return;
    const source = context?.billingAddress || null;
    if (seededFrom.current === source) return;
    seededFrom.current = source;
    seedForm(source);
  }, [editing, states, context?.billingAddress, seedForm]);

  useEffect(() => {
    if (timeLeft === 0 && (phase === 'ready' || phase === 'saved')) endLink('ERR_ADDRESS_LINK_EXPIRED');
  }, [timeLeft, phase, endLink]);

  const onSubmit = async ({ billing }) => {
    setSaving(true);
    try {
      const data = await addressLinkService.saveAddress({
        token,
        address: {
          houseNo: billing.houseNo.trim(),
          area: billing.area.trim(),
          landmark: (billing.landmark || '').trim(),
          city: billing.city.trim(),
          state: billing.state,
          pinCode: billing.pinCode,
        },
      });
      setContext((current) => ({
        ...current,
        billingAddress: data.billingAddress,
        isComplete: true,
        expiresAt: data.expiresAt || current?.expiresAt,
      }));
      setEditing(false);
      setPhase('saved');
      // The confirmation replaces a long form: show it from the top.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      handleFailure(err, { onOther: (error) => showToast(error?.message || 'Could not save the address. Please try again.') });
    } finally {
      setSaving(false);
    }
  };

  const startEditing = () => {
    seededFrom.current = null;
    setEditing(true);
    setPhase('ready');
  };

  if (phase === 'loading') return <Spinner fullPage />;

  if (phase === 'no-token') {
    return (
      <PageShell>
        <MessageCard
          icon={Link2Off}
          title="Open your address link"
          body="Please open the link from the email the centre sent you, or scan the QR code at the centre."
        />
      </PageShell>
    );
  }

  if (phase === 'dead') {
    const copy = DEAD_LINK[deadCode] || DEAD_LINK.ERR_ADDRESS_LINK_INVALID;
    return (
      <PageShell>
        <MessageCard icon={Link2Off} title={copy.title} body={copy.body} />
      </PageShell>
    );
  }

  if (phase === 'error') {
    return (
      <PageShell>
        <MessageCard
          icon={RefreshCw}
          title="We couldn't open this page"
          body="Please check your connection and try again."
          action={<button type="button" className={PRIMARY_BUTTON} onClick={load}>Try again</button>}
        />
      </PageShell>
    );
  }

  const address = context?.billingAddress;
  const lastUpdated = formatDate(address?.addedOn);

  return (
    <PageShell>
      <div className="mb-5">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
          {context?.firstName ? `Hi ${context.firstName},` : 'Hi,'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {context?.maskedPhone
            ? `Account ending ${String(context.maskedPhone).slice(-4)}. Not you? Please tell the centre.`
            : 'Please check this is your account before you continue.'}
        </p>
        {timeLeft !== null && (
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-[#045692]">
            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
            {`Link valid for ${formatTimeLeft(timeLeft)}`}
          </p>
        )}
      </div>

      {phase === 'saved' && (
        <MessageCard
          icon={CircleCheck}
          tone="success"
          title="Student address saved"
          body="The centre can see your updated address now. You can close this page."
          action={(
            <div className="text-left">
              <div className="mb-4 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-800">
                {formatAddressLines(address).map((line) => <p key={line}>{line}</p>)}
              </div>
              <button type="button" className={SECONDARY_BUTTON} onClick={startEditing}>
                <Pencil className="w-4 h-4" aria-hidden="true" />
                Make a correction
              </button>
            </div>
          )}
        />
      )}

      {phase === 'ready' && !editing && (
        <section className="bg-white border border-[#B8E5FF] rounded-xl shadow-sm overflow-hidden">
          <header className="flex items-center gap-3 px-4 pt-4 pb-3 md:px-5 border-b border-gray-100">
            <div className="p-2 bg-blue-50 rounded-lg shrink-0">
              <ReceiptText className="w-5 h-5 text-blue-600" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 leading-tight">Student Address</h2>
              <p className="mt-0.5 text-xs md:text-sm text-gray-500">For admission records and billing</p>
            </div>
          </header>
          <div className="px-4 py-4 md:px-5 text-sm text-gray-800 space-y-0.5">
            {formatAddressLines(address).map((line) => <p key={line}>{line}</p>)}
            {lastUpdated && <p className="pt-2 text-xs text-gray-500">{`Last updated ${lastUpdated}`}</p>}
          </div>
          <footer className="px-4 py-3 md:px-5 bg-gray-50 border-t border-gray-100">
            <button type="button" className={PRIMARY_BUTTON} onClick={startEditing}>
              <Pencil className="w-4 h-4" aria-hidden="true" />
              Update
            </button>
          </footer>
        </section>
      )}

      {phase === 'ready' && editing && (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {address && !context?.isComplete && (
            <p className="mb-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
              The address we have for you is incomplete. Please check it and fill in what is missing.
            </p>
          )}
          <AddressDetails
            register={register}
            setValue={setValue}
            errors={errors}
            userDetails={userDetails}
            setUserDetails={setUserDetails}
            states={states || []}
            title="Student Address"
            subtitle="For admission records and billing"
            fieldPrefix="billing."
            addressKey="billing"
            icon={ReceiptText}
          />
          <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white/95 border-t border-gray-100 md:static md:mx-0 md:px-0 md:bg-transparent md:border-0 flex flex-col-reverse md:flex-row gap-2 md:justify-end">
            {address && context?.isComplete && (
              <button type="button" className={SECONDARY_BUTTON} onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </button>
            )}
            <button type="submit" className={PRIMARY_BUTTON} disabled={saving}>
              {saving ? 'Saving…' : 'Save Student Address'}
            </button>
          </div>
        </form>
      )}
    </PageShell>
  );
}

export default BillingAddressPage;
